import AppContext from 'App/AppContext';
import { FormStructureMutationResult, FormVersionConflictCode } from 'models/tasks/formVersion';
import TaskType from 'models/tasks/taskType';
import TaskTypeFormElement from 'models/tasks/taskTypeFormElement';
import { useContext, useEffect, useRef, useState } from 'react';
import { apiDiscardFormEditSession, apiUpdateFormStructure } from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';
import AppError from 'utils/appError';
import { newGuid } from 'utils/guid';

export type FormStructureAutosaveState = 'saved' | 'pending' | 'saving' | 'error';
export type FormStructureAutosaveFailure = 'transport' | 'conflict';

interface IUseFormStructureAutosaveProps {
  taskType: TaskType | undefined;
  onAccepted: (result: FormStructureMutationResult, formElements: TaskTypeFormElement[]) => void;
  onError: (error: unknown) => void;
}

interface IQueuedSnapshot {
  formElements: TaskTypeFormElement[];
  mutationId: string;
}

export interface IFormStructureAutosave {
  state: FormStructureAutosaveState;
  failure?: FormStructureAutosaveFailure;
  canDiscard: boolean;
  enqueue: (formElements: TaskTypeFormElement[]) => void;
  discardSession: () => Promise<boolean>;
  flush: () => Promise<boolean>;
  getCurrentTaskType: () => TaskType | undefined;
  reapply: (taskType: TaskType, formElements: TaskTypeFormElement[]) => Promise<boolean>;
  rebase: (taskType: TaskType) => void;
  retry: () => Promise<boolean>;
}

const debounceMilliseconds = 1500;

const isConflictResponse = (error: unknown): error is AppError => error instanceof AppError && error.code === '409';

const isStructureChangedConflict = (error: unknown): boolean =>
  isConflictResponse(error) && error.data?.code === FormVersionConflictCode.StructureChanged;

// The lock coordinator's "another mutation is in progress" is the one 409 a plain retry can win: the
// lease expires. Every other 409 (archived, a stale definition pin, a moved head) needs a reload, so
// it gets the Resolve path instead of a Retry button that re-posts the same payload forever.
const isRetryableBusyConflict = (error: unknown): boolean =>
  isConflictResponse(error) && error.data?.code === FormVersionConflictCode.MutationBusy;

interface IAcceptedFieldIdentity {
  fieldId: number;
  fieldKey?: string;
  kpiId?: number;
  kpiDefinitionRevisionId?: number;
  kpiDefinitionRevisionRowVersion?: string;
}

const toAcceptedFieldIdentity = (element: TaskTypeFormElement): IAcceptedFieldIdentity => ({
  fieldId: element.fieldId,
  fieldKey: element.fieldKey,
  kpiId: element.kpiId,
  kpiDefinitionRevisionId: element.kpiDefinitionRevisionId,
  kpiDefinitionRevisionRowVersion: element.kpiDefinitionRevisionRowVersion,
});

const rebaseFormElementIdentities = (
  submittedElements: TaskTypeFormElement[],
  targetElements: TaskTypeFormElement[],
  authoritativeTaskType: TaskType,
): TaskTypeFormElement[] => {
  const authoritativeElements = authoritativeTaskType.form ?? [];
  const identityByClientKey = new Map<string, IAcceptedFieldIdentity>();
  const claimedElements = new Set<TaskTypeFormElement>();
  const submittedNewElements: TaskTypeFormElement[] = [];

  // An identified element claims its authoritative row by stable key first, then by id; a row can
  // be claimed once, so a corrupt duplicate can never spread its identity further.
  submittedElements.forEach((submittedElement) => {
    if (!submittedElement.fieldKey && !(submittedElement.fieldId > 0)) {
      submittedNewElements.push(submittedElement);

      return;
    }

    const authoritativeElement = submittedElement.fieldKey
      ? authoritativeElements.find((element) => element.fieldKey === submittedElement.fieldKey)
      : authoritativeElements.find((element) => element.fieldId === submittedElement.fieldId);
    if (!authoritativeElement || claimedElements.has(authoritativeElement)) {
      return;
    }

    claimedElements.add(authoritativeElement);
    identityByClientKey.set(submittedElement.key, toAcceptedFieldIdentity(authoritativeElement));
  });

  // A brand-new element cannot be found by key, and its INDEX is not usable either: an in-place
  // amend returns the existing fields in storage order with the new rows appended, so a new element
  // reordered before the save sits at a different index than its row. What holds on both the amend
  // and the fork path is that new rows are appended in submission order - so the k-th new submitted
  // element is the k-th unclaimed authoritative row. Criterion rows are excluded: the server may
  // re-add generated fields the client never submitted, and those are never a client-created field.
  const unclaimedElements = authoritativeElements.filter(
    (element) => !claimedElements.has(element) && !element.criterionKey,
  );
  submittedNewElements.forEach((submittedElement, index) => {
    const authoritativeElement = unclaimedElements[index];
    if (!authoritativeElement) {
      return;
    }

    identityByClientKey.set(submittedElement.key, toAcceptedFieldIdentity(authoritativeElement));
  });

  return targetElements.map((element) => {
    const rebasedElement = element.clone();
    rebasedElement.taskTypeId = authoritativeTaskType.taskTypeId;
    const acceptedIdentity = identityByClientKey.get(element.key);
    if (acceptedIdentity) {
      rebasedElement.fieldId = acceptedIdentity.fieldId;
      rebasedElement.fieldKey = acceptedIdentity.fieldKey;
      if (rebasedElement.kpiId === acceptedIdentity.kpiId) {
        rebasedElement.kpiDefinitionRevisionId = acceptedIdentity.kpiDefinitionRevisionId;
        rebasedElement.kpiDefinitionRevisionRowVersion = acceptedIdentity.kpiDefinitionRevisionRowVersion;
      }
    }

    return rebasedElement;
  });
};

export const useFormStructureAutosave = (props: IUseFormStructureAutosaveProps): IFormStructureAutosave => {
  const appContext = useContext(AppContext);
  const [state, setState] = useState<FormStructureAutosaveState>('saved');
  const [failure, setFailure] = useState<FormStructureAutosaveFailure | undefined>(undefined);
  const [canDiscard, setCanDiscard] = useState<boolean>(false);
  const failureRef = useRef<FormStructureAutosaveFailure | undefined>(undefined);
  const taskTypeRef = useRef<TaskType | undefined>(props.taskType);
  const acceptedRef = useRef(props.onAccepted);
  const errorRef = useRef(props.onError);
  const queuedRef = useRef<IQueuedSnapshot | undefined>(undefined);
  const failedRef = useRef<IQueuedSnapshot | undefined>(undefined);
  const activeRef = useRef<Promise<boolean> | undefined>(undefined);
  const timerRef = useRef<number | undefined>(undefined);
  const mountedRef = useRef(true);
  const flushRef = useRef<() => Promise<boolean>>(() => Promise.resolve(true));
  const editSessionIdRef = useRef(newGuid());
  const sessionFamilyIdRef = useRef(props.taskType?.taskTypeFamilyId);
  const sessionTaskTypeIdRef = useRef(props.taskType?.taskTypeId);
  const baselineDefinitionHashRef = useRef(props.taskType?.definitionHash);
  const lastAcceptedDefinitionHashRef = useRef(props.taskType?.definitionHash);
  const hasAcceptedSessionMutationRef = useRef(false);
  const discardMutationIdRef = useRef<string | undefined>(undefined);

  const setAutosaveState = (nextState: FormStructureAutosaveState) => {
    if (mountedRef.current) {
      setState(nextState);
    }
  };

  const setAutosaveFailure = (nextFailure: FormStructureAutosaveFailure | undefined) => {
    failureRef.current = nextFailure;
    if (mountedRef.current) {
      setFailure(nextFailure);
    }
  };

  const rebaseQueuedSnapshot = (submittedElements: TaskTypeFormElement[], authoritativeTaskType: TaskType) => {
    const queuedSnapshot = queuedRef.current;
    if (!queuedSnapshot) {
      return;
    }

    queuedRef.current = {
      ...queuedSnapshot,
      formElements: rebaseFormElementIdentities(submittedElements, queuedSnapshot.formElements, authoritativeTaskType),
    };
  };

  useEffect(() => {
    const selectionChanged = props.taskType?.taskTypeFamilyId
      ? props.taskType.taskTypeFamilyId !== sessionFamilyIdRef.current
      : props.taskType?.taskTypeId !== sessionTaskTypeIdRef.current;
    if (selectionChanged || !hasAcceptedSessionMutationRef.current) {
      sessionFamilyIdRef.current = props.taskType?.taskTypeFamilyId;
      sessionTaskTypeIdRef.current = props.taskType?.taskTypeId;
      baselineDefinitionHashRef.current = props.taskType?.definitionHash;
      lastAcceptedDefinitionHashRef.current = props.taskType?.definitionHash;
      hasAcceptedSessionMutationRef.current = false;
      discardMutationIdRef.current = undefined;
      if (selectionChanged) {
        editSessionIdRef.current = newGuid();
        //a failure belongs to the form it happened on: carried over, every edit of the newly selected
        //form would be held back as well (enqueue parks edits while a conflict stands)
        if (timerRef.current !== undefined) {
          window.clearTimeout(timerRef.current);
          timerRef.current = undefined;
        }
        queuedRef.current = undefined;
        failedRef.current = undefined;
        setAutosaveFailure(undefined);
        setAutosaveState('saved');
      }
      setCanDiscard(false);
    }

    taskTypeRef.current = props.taskType;
    acceptedRef.current = props.onAccepted;
    errorRef.current = props.onError;
  }, [props.taskType, props.onAccepted, props.onError]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      if (timerRef.current !== undefined) {
        window.clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }

      void flushRef.current();
    };
  }, []);

  const processQueue = async (): Promise<boolean> => {
    if (activeRef.current) {
      return activeRef.current;
    }

    const operation = (async (): Promise<boolean> => {
      while (queuedRef.current) {
        const snapshot = queuedRef.current;
        queuedRef.current = undefined;
        const taskType = taskTypeRef.current;
        if (!taskType) {
          setAutosaveState('error');
          setAutosaveFailure('transport');
          errorRef.current(new Error('A form must be selected before its structure can be saved.'));
          failedRef.current = snapshot;

          return false;
        }

        try {
          setAutosaveState('saving');
          const accessToken = await appContext.getAccessToken(apiRequest.scopes);
          const result = await apiUpdateFormStructure(
            taskType,
            snapshot.formElements,
            editSessionIdRef.current,
            accessToken,
            snapshot.mutationId,
          );
          failedRef.current = undefined;
          setAutosaveFailure(undefined);
          taskTypeRef.current = result.taskType;
          lastAcceptedDefinitionHashRef.current = result.taskType.definitionHash;
          hasAcceptedSessionMutationRef.current =
            hasAcceptedSessionMutationRef.current ||
            (!!baselineDefinitionHashRef.current &&
              result.taskType.definitionHash !== baselineDefinitionHashRef.current);
          setCanDiscard(
            hasAcceptedSessionMutationRef.current &&
              !result.taskType.firstUsedOn &&
              !!baselineDefinitionHashRef.current &&
              result.taskType.definitionHash !== baselineDefinitionHashRef.current,
          );
          const acceptedElements = rebaseFormElementIdentities(
            snapshot.formElements,
            snapshot.formElements,
            result.taskType,
          );
          rebaseQueuedSnapshot(snapshot.formElements, result.taskType);
          acceptedRef.current(result, acceptedElements);
          setAutosaveState(queuedRef.current ? 'pending' : 'saved');
        } catch (error) {
          failedRef.current = snapshot;
          setAutosaveState('error');
          const nextFailure =
            isConflictResponse(error) && !isRetryableBusyConflict(error) ? 'conflict' : 'transport';
          setAutosaveFailure(nextFailure);
          // The structure-changed conflict has its own resolve dialog; every other failure gets the
          // toast, so a non-retryable 409 (archived, stale pin) still tells the user WHY.
          if (!isStructureChangedConflict(error)) {
            errorRef.current(error);
          }

          return false;
        }
      }

      return true;
    })();

    activeRef.current = operation;
    try {
      return await operation;
    } finally {
      activeRef.current = undefined;
    }
  };

  const enqueue = (formElements: TaskTypeFormElement[]) => {
    if (failureRef.current === 'conflict') {
      failedRef.current = {
        formElements: formElements.map((element) => element.clone()),
        mutationId: newGuid(),
      };
      setAutosaveState('error');

      return;
    }

    queuedRef.current = {
      formElements: formElements.map((element) => element.clone()),
      mutationId: newGuid(),
    };
    failedRef.current = undefined;
    setAutosaveFailure(undefined);
    setAutosaveState('pending');
    if (timerRef.current !== undefined) {
      window.clearTimeout(timerRef.current);
    }

    timerRef.current = window.setTimeout(() => {
      timerRef.current = undefined;
      void processQueue();
    }, debounceMilliseconds);
  };

  //True only when nothing is left to save. Edits held after a failed save (failedRef) count: a
  //transport failure gets one more attempt here, a conflict stays with the user, who resolves it or
  //confirms the loss. Callers close or switch forms on true, so a true with held edits loses them.
  const flush = async (): Promise<boolean> => {
    if (timerRef.current !== undefined) {
      window.clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }

    if (failedRef.current && !queuedRef.current && failureRef.current !== 'conflict') {
      queuedRef.current = failedRef.current;
      failedRef.current = undefined;
    }

    const saved = await processQueue();

    return saved && !failedRef.current && !queuedRef.current;
  };

  flushRef.current = flush;

  const getCurrentTaskType = () => taskTypeRef.current;

  const rebase = (taskType: TaskType) => {
    if (timerRef.current !== undefined) {
      window.clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }

    queuedRef.current = undefined;
    failedRef.current = undefined;
    taskTypeRef.current = taskType;
    sessionFamilyIdRef.current = taskType.taskTypeFamilyId;
    sessionTaskTypeIdRef.current = taskType.taskTypeId;
    baselineDefinitionHashRef.current = taskType.definitionHash;
    lastAcceptedDefinitionHashRef.current = taskType.definitionHash;
    hasAcceptedSessionMutationRef.current = false;
    discardMutationIdRef.current = undefined;
    editSessionIdRef.current = newGuid();
    setCanDiscard(false);
    setAutosaveFailure(undefined);
    setAutosaveState('saved');
  };

  const retry = async (): Promise<boolean> => {
    if (failureRef.current === 'conflict') {
      return false;
    }

    if (failedRef.current && !queuedRef.current) {
      queuedRef.current = failedRef.current;
      failedRef.current = undefined;
      setAutosaveFailure(undefined);
      setAutosaveState('pending');
    }

    return await flush();
  };

  const reapply = async (taskType: TaskType, formElements: TaskTypeFormElement[]): Promise<boolean> => {
    taskTypeRef.current = taskType;
    sessionFamilyIdRef.current = taskType.taskTypeFamilyId;
    sessionTaskTypeIdRef.current = taskType.taskTypeId;
    baselineDefinitionHashRef.current = taskType.definitionHash;
    lastAcceptedDefinitionHashRef.current = taskType.definitionHash;
    hasAcceptedSessionMutationRef.current = false;
    discardMutationIdRef.current = undefined;
    editSessionIdRef.current = newGuid();
    failedRef.current = undefined;
    queuedRef.current = {
      formElements: formElements.map((element) => element.clone()),
      mutationId: newGuid(),
    };
    setCanDiscard(false);
    setAutosaveFailure(undefined);
    setAutosaveState('pending');

    return await flush();
  };

  const discardSession = async (): Promise<boolean> => {
    if (!(await flush())) {
      return false;
    }

    const taskType = taskTypeRef.current;
    const baselineDefinitionHash = baselineDefinitionHashRef.current;
    if (
      !taskType?.taskTypeFamilyId ||
      !taskType.definitionHash ||
      !baselineDefinitionHash ||
      taskType.definitionHash === baselineDefinitionHash ||
      !hasAcceptedSessionMutationRef.current
    ) {
      setCanDiscard(false);

      return true;
    }

    try {
      setAutosaveState('saving');
      discardMutationIdRef.current ??= newGuid();
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const result = await apiDiscardFormEditSession(
        taskType,
        baselineDefinitionHash,
        editSessionIdRef.current,
        accessToken,
        discardMutationIdRef.current,
      );
      taskTypeRef.current = result.taskType;
      acceptedRef.current(result, result.taskType.form ?? []);
      sessionFamilyIdRef.current = result.taskType.taskTypeFamilyId;
      sessionTaskTypeIdRef.current = result.taskType.taskTypeId;
      baselineDefinitionHashRef.current = result.taskType.definitionHash;
      lastAcceptedDefinitionHashRef.current = result.taskType.definitionHash;
      hasAcceptedSessionMutationRef.current = false;
      discardMutationIdRef.current = undefined;
      editSessionIdRef.current = newGuid();
      setCanDiscard(false);
      setAutosaveFailure(undefined);
      setAutosaveState('saved');

      return true;
    } catch (error) {
      if (isConflictResponse(error) && !isRetryableBusyConflict(error)) {
        setCanDiscard(false);
        setAutosaveFailure('conflict');
        if (!isStructureChangedConflict(error)) {
          errorRef.current(error);
        }
      } else {
        setAutosaveFailure('transport');
        errorRef.current(error);
      }
      setAutosaveState('error');

      return false;
    }
  };

  return {
    state,
    failure,
    canDiscard,
    enqueue,
    discardSession,
    flush,
    getCurrentTaskType,
    reapply,
    rebase,
    retry,
  };
};
